/**
 * Slip test page — verify Thai bank transfer slip via SlipOK API
 * Setup: wrangler secret put SLIPOK_API_KEY  (สมัครฟรี slipok.com 100/เดือน)
 *
 * Test flow:
 *   1. Admin paste image URL ของสลิป (จาก FB / cloud)
 *   2. (optional) ใส่ยอดที่ลูกค้าควรโอน
 *   3. กดทดสอบ → call SlipOK API → return raw + check match
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Send, Receipt, CheckCircle2, AlertTriangle } from 'lucide-react';
import { SlipApi, type SlipTestResult, type Brand } from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

export default function SlipTestPage() {
  const [imageUrl, setImageUrl] = useState('');
  const [expectedAmountBaht, setExpectedAmountBaht] = useState('999');
  const [brand, setBrand] = useState<Brand>('scrub');

  const mut = useMutation({
    mutationFn: () => {
      const input: { imageUrl: string; expectedAmount?: number; brand?: Brand } = {
        imageUrl,
        brand,
      };
      if (expectedAmountBaht) {
        input.expectedAmount = Math.round(Number(expectedAmountBaht) * 100);
      }
      return SlipApi.test(input);
    },
  });

  return (
    <div className="space-y-4">
      <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3 text-sm">
        <div className="flex items-center gap-2 font-semibold mb-1">
          <Receipt size={16} /> ทดสอบตรวจสลิปโอนเงิน (SlipOK API)
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          วิธีใช้: 1) วาง URL รูปสลิป (จาก FB / cloud / R2) → 2) ใส่ยอดที่ลูกค้าควรโอน → 3) กดทดสอบ.
          ระบบจะตรวจ <strong>ยอดเงิน</strong> + <strong>เลขบัญชีปลายทาง</strong> (เทียบกับ "ณ ยา" ที่ตั้งไว้ใน Brand Settings).
          <br />
          ✨ <strong>ใช้งานได้ทันที</strong> — ระบบใช้ Workers AI vision OCR (ฟรี ไม่ต้องสมัคร).
          ถ้าต้องการความแม่นยำสูงสุด ตั้ง{' '}
          <code className="bg-white px-1 rounded border text-[10px]">SLIPOK_API_KEY</code>{' '}
          เพิ่ม (
          <a href="https://slipok.com" target="_blank" rel="noreferrer" className="underline text-blue-700">
            slipok.com
          </a>{' '}
          ฟรี 100/เดือน) — ระบบ fallback ใช้ SlipOK ก่อนถ้ามี.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3 border border-border rounded-lg p-4">
          <h3 className="font-semibold text-sm">Input</h3>
          <div>
            <label className="text-xs text-muted-foreground">URL รูปสลิป</label>
            <Input
              placeholder="https://scontent.fbkk... หรือ https://r2.../slip.jpg"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">ยอดที่ควรโอน (฿)</label>
              <Input
                type="number"
                placeholder="999"
                value={expectedAmountBaht}
                onChange={(e) => setExpectedAmountBaht(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">แบรนด์</label>
              <select
                className="w-full border border-input rounded-md h-9 px-3 text-sm"
                value={brand}
                onChange={(e) => setBrand(e.target.value as Brand)}
              >
                <option value="scrub">Cher Scrub</option>
                <option value="bow">Cherlyn</option>
              </select>
            </div>
          </div>
          <Button
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !imageUrl}
            className="w-full"
          >
            <Send size={14} className="mr-1" />
            {mut.isPending ? 'กำลังตรวจ…' : 'ทดสอบสลิป'}
          </Button>
        </div>

        <div className="border border-border rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-sm">ผลตรวจ</h3>
          {mut.error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              ❌ {String(mut.error)}
            </div>
          )}
          {mut.data ? <SlipResultView data={mut.data} /> : (
            <div className="text-sm text-muted-foreground">
              ใส่ URL + กดทดสอบเพื่อดูผล
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SlipResultView({ data }: { data: SlipTestResult }) {
  const c = data.check;
  const allMatch = c.amountMatch !== false && c.accountMatch !== false;

  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center gap-2 text-xs">
        <Badge variant="outline">
          {data.strategy === 'ai_vision' ? '🤖 ตรวจด้วย AI vision (ฟรี)' : '🔬 ตรวจด้วย SlipOK API'}
        </Badge>
      </div>
      <div
        className={`p-3 rounded border ${
          allMatch
            ? 'bg-emerald-50 border-emerald-200'
            : 'bg-amber-50 border-amber-200'
        }`}
      >
        <div className="flex items-center gap-2 font-semibold">
          {allMatch ? (
            <>
              <CheckCircle2 size={18} className="text-emerald-700" />
              <span>สลิปผ่านการตรวจ</span>
            </>
          ) : (
            <>
              <AlertTriangle size={18} className="text-amber-700" />
              <span>มีข้อมูลไม่ตรง — รอแอดมินตรวจสอบ</span>
            </>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <CheckRow
          label="ยอดเงิน"
          match={c.amountMatch}
          expected={c.expectedAmount != null ? `${(c.expectedAmount / 100).toLocaleString()} ฿` : '(ไม่ระบุ)'}
          received={c.verifiedAmount != null ? `${(c.verifiedAmount / 100).toLocaleString()} ฿` : '-'}
        />
        <CheckRow
          label="บัญชีปลายทาง"
          match={c.accountMatch}
          expected={`${c.expectedAccountName ?? '-'} ${c.expectedAccount ? `(...${c.expectedAccount.slice(-4)})` : ''}`}
          received={`${c.receivedAccountName ?? '-'} ${c.receivedAccount ? `(${c.receivedAccount})` : ''}`}
        />
      </div>

      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
          🔽 ดูข้อมูลดิบจาก SlipOK
        </summary>
        <pre className="mt-2 p-2 bg-muted/30 border rounded font-mono whitespace-pre-wrap overflow-auto max-h-80">
          {JSON.stringify(data.raw, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function CheckRow({
  label,
  match,
  expected,
  received,
}: {
  label: string;
  match: boolean | null;
  expected: string;
  received: string;
}) {
  return (
    <div className="border border-border rounded p-2 text-xs">
      <div className="flex items-center justify-between mb-1">
        <span className="font-medium">{label}</span>
        {match === true && <Badge className="bg-emerald-100 text-emerald-700">ตรง</Badge>}
        {match === false && <Badge className="bg-red-100 text-red-700">ไม่ตรง</Badge>}
        {match == null && <Badge variant="outline">—</Badge>}
      </div>
      <div className="grid grid-cols-2 gap-2 text-muted-foreground">
        <div>
          <div className="text-[10px]">ที่ควรเป็น</div>
          <div className="text-foreground">{expected}</div>
        </div>
        <div>
          <div className="text-[10px]">ที่ได้จากสลิป</div>
          <div className={match === false ? 'text-red-700' : 'text-foreground'}>
            {received}
          </div>
        </div>
      </div>
    </div>
  );
}
